import { rgbColorToCssHexString } from '../colors';

/**
 * Generates the elevation gradient color bands used for elevation rendering.
 * This creates a gradient from blue (below reference) to green
 * with black center (reference) and from green to red (above reference).
 *
 * @param bandCount The number of bands to generate
 * @returns Array of tuples containing [percentage, hexcolor] pairs for the gradient
 */
export function getPointCloudElevationGradient(
  bandCount: number
): [number, string][] {
  // The logical minimum band count is 2 because it includes the out of tolerance band as well.
  // Band count is only a representation of one side of the total bands separated by the center.
  // So band count of 2 gives us 10 bands total.  The color bands come in pairs to prevent a gradient.
  // So 2 blue and 2 green bands before the center, 2 black center bands, 2 green and 2 red above the center.
  // eslint-disable-next-line no-magic-numbers
  if (bandCount < 2) {
    return [];
  }

  // Generate below colors (blue to green gradient)
  const belowColors: string[] = [];
  for (let i = 0; i < bandCount; i++) {
    // eslint-disable-next-line no-magic-numbers
    const progress = Math.floor((i / (bandCount - 1)) * 255);
    const red = 0;
    const green = progress;
    // eslint-disable-next-line no-magic-numbers
    const blue = 255 - progress;
    belowColors.push(rgbColorToCssHexString({ r: red, g: green, b: blue }));
  }

  // Generate above colors (green to red gradient)
  const aboveColors: string[] = [];
  for (let i = 0; i < bandCount; i++) {
    // eslint-disable-next-line no-magic-numbers
    const progress = Math.floor((i / (bandCount - 1)) * 255);
    const red = progress;
    // eslint-disable-next-line no-magic-numbers
    const green = 255 - progress;
    const blue = 0;
    aboveColors.push(rgbColorToCssHexString({ r: red, g: green, b: blue }));
  }

  const black = rgbColorToCssHexString({ r: 0, g: 0, b: 0 });

  const centerStripWidth = 0.01;
  const center = 0.5;
  // eslint-disable-next-line no-magic-numbers
  const centerStart = center - centerStripWidth / 2;
  // eslint-disable-next-line no-magic-numbers
  const centerEnd = center + centerStripWidth / 2;

  const bandWidth = center / belowColors.length;

  const bands: [number, string][] = [];

  // Below colors (from 0 to centerStart)
  belowColors.forEach((color, i) => {
    const start = i * bandWidth;
    const end =
      i === belowColors.length - 1 ? centerStart : (i + 1) * bandWidth;
    bands.push([start, color], [end, color]);
  });

  // Center black strip
  bands.push([centerStart, black], [centerEnd, black]);

  // Above colors (from centerEnd to 1)
  aboveColors.forEach((color, i) => {
    const start = i === 0 ? centerEnd : center + i * bandWidth;
    const end = center + (i + 1) * bandWidth;
    bands.push([start, color], [Math.min(1, end), color]);
  });

  return bands;
}
